import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  SelectChangeEvent,
  Typography,
  Switch,
  FormHelperText,
} from "@mui/material";
import * as Yup from "yup";
import { isString, useFormik } from "formik";
import {
  useAddSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useLazyGetSubscriptionByIdQuery,
} from "../../services/subscription";
import DeleteIcon from "@mui/icons-material/Delete";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody, Subscription } from "../../types/General";
import { Loader, showError, showToast } from "../../constants";
import { isNumber } from "../../utils/validations";
import {
  ARABIC_FIELD_CLASS,
  arabicInputProps,
  withArabicFieldClass,
} from "../../utils/arabicField";
import useTranslation from "../../hooks/Translation";

const AddSubscriptionPlan = () => {
  const { id } = useParams();
  const location = useLocation();
  const { state } = location;
  const [addSubscription] = useAddSubscriptionMutation();
  const [updateSubscription] = useUpdateSubscriptionMutation();
  const [subscriptionDetails] = useLazyGetSubscriptionByIdQuery();
  const navigate = useNavigate();
  const [subsById, setSubsById] = useState<Subscription>();
  const [selectedFeature, setSelectedFeature] = useState<any>();
  const [receiver, setReceiver] = useState<string>("");
  const [receiverAr, setReceiverAr] = useState<string>("");
  const [validityUnit, setValidityUnit] = useState<
    "months" | "days" | "hours" | "minutes"
  >("months");
  const [error, setError] = useState(false);

  const durationMaps: Record<
    "months" | "days" | "hours" | "minutes",
    { ar: Record<string, string>; en: Record<string, string>; values: number[] }
  > = {
    months: {
      values: [1, 2, 3, 4, 5, 6],
      ar: {
        1: "شهر",
        2: "شهرين",
        3: "٣ أشهر",
        4: "٤ أشهر",
        5: "٥ أشهر",
        6: "٦ أشهر",
      },
      en: {
        شهر: "1",
        شهرين: "2",
        "٣ أشهر": "3",
        "٤ أشهر": "4",
        "٥ أشهر": "5",
        "٦ أشهر": "6",
      },
    },
    days: {
      values: [1, 7, 15],
      ar: { 1: "يوم", 7: "٧ أيام", 15: "١٥ يوماً" },
      en: { يوم: "1", "٧ أيام": "7", "١٥ يوماً": "15" },
    },
    hours: {
      values: [1, 2, 6, 12],
      ar: {
        1: "ساعة",
        2: "ساعتين",
        6: "٦ ساعات",
        12: "١٢ ساعة",
      },
      en: {
        ساعة: "1",
        ساعتين: "2",
        "٦ ساعات": "6",
        "١٢ ساعة": "12",
      },
    },
    minutes: {
      values: [1, 5, 10, 15, 30],
      ar: {
        1: "دقيقة",
        5: "٥ دقائق",
        10: "١٠ دقائق",
        15: "١٥ دقيقة",
        30: "٣٠ دقيقة",
      },
      en: {
        دقيقة: "1",
        "٥ دقائق": "5",
        "١٠ دقائق": "10",
        "١٥ دقيقة": "15",
        "٣٠ دقيقة": "30",
      },
    },
  };
  const activeDuration = durationMaps[validityUnit];
  const timeDurations = activeDuration.ar;
  const timeDurations2 = activeDuration.en;
  const [featureEnabled, setFeatureEnabled] = useState(false); // Set to true or false based on your logic
  const [loading, setLoading] = useState(false);

  const handleSwitchChange = () => {
    setFeatureEnabled(!featureEnabled);
    // Add any other logic you want to execute when the switch changes state
  };

  const [switchValue, setSwitchValue] = useState(1);
  console.log(switchValue);

  const handleSwitchClick = () => {
    setSwitchValue((prevValue) => (prevValue === 1 ? 2 : 1));
  };

  const handleSwitchToggle = (id: string) => {
    setSelectedFeature(id);
    // Implement your logic to send the feature id in the body
    console.log("Feature ID:", id);
  };
  const translate = useTranslation();
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      price: subsById?.price ?? "",
      description: subsById?.description || "",
      planName: subsById?.planName || "",
      description_ar: subsById?.description_ar || "",
      planName_ar: subsById?.planName_ar || "",
      normalListing: subsById?.normalCredit ?? "",
      highlightedListing: subsById?.highlightCredit ?? "",
      acceptRequest: subsById?.acceptRequests ?? "",
      validity: subsById?.validity || "",
    },
    validationSchema: Yup.object({
      price: Yup.number()
        .typeError(translate?.error?.required || "Price is required")
        .required(translate?.error?.required || "")
        .min(0, translate?.error?.required || "Price cannot be negative"),

      normalListing: Yup.number()
        .typeError(translate?.error?.required || "Required")
        .required(translate?.error?.required || "")
        .min(0, "Cannot be negative")
        .max(999999, translate?.error?.maxValidation20 || ""),

      highlightedListing: Yup.number()
        .typeError(translate?.error?.required || "Required")
        .required(translate?.error?.required || "")
        .min(0, "Cannot be negative")
        .max(999999, translate?.error?.maxValidation20 || ""),

      acceptRequest: Yup.number()
        .typeError(translate?.error?.required || "Required")
        .required(translate?.error?.required || "")
        .min(0, "Cannot be negative")
        .max(999999, translate?.error?.maxValidation20 || ""),

      description: Yup.string()
        .required(translate?.error?.required || "")
        .max(450, translate?.error?.maxValidation450 || "")
        .min(2, translate?.error?.minValidation2 || ""),
      description_ar: Yup.string()
        .required(translate?.error?.required || "")
        .max(450, translate?.error?.maxValidation450 || "")
        .min(2, translate?.error?.minValidation2 || "")
        .test("no-spaces-only", translate?.error?.noSpacesOnly || "", (value) => {
          return value ? value.trim().length > 0 : true;
        }),
      planName: Yup.string()
        .required(translate?.error?.required || "")
        .max(20, translate?.error?.maxValidation20 || "")
        .min(2, translate?.error?.minValidation2 || ""),
      planName_ar: Yup.string()
        .required(translate?.error?.required || "")
        .max(20, translate?.error?.maxValidation20 || "")
        .min(2, translate?.error?.minValidation2 || "")
        .test("no-spaces-only", translate?.error?.noSpacesOnly || "", (value) => {
          return value ? value.trim().length > 0 : true;
        }),
      validity: Yup.number()
        .typeError(translate?.error?.required || "Validity is required")
        .required(translate?.error?.required || "Validity is required")
        .oneOf(
          activeDuration.values,
          translate?.error?.required || "Select validity"
        ),
    }),
    onSubmit: async () => {
      formik.setSubmitting(true);

      const data = {
        planName: formik?.values?.planName,
        description: formik?.values?.description,
        planName_ar: formik?.values?.planName_ar,
        description_ar: formik?.values?.description_ar,
        validity: Number(receiver),
        validityUnit,
        validity_ar: receiverAr,
        feature: featureEnabled,
        price: Number(formik?.values?.price),
        normalCredit: Number(formik?.values?.normalListing),
        highlightCredit: Number(formik?.values?.highlightedListing),
        acceptRequests: Number(formik?.values?.acceptRequest),
      };

      console.log(data,"body for plan");

      if (id && id !== "add") {
        try {
          setLoading(true);
          let encryptedBody = generateEncryptedKeyBody(data) as CommonBody;
          const response = await updateSubscription({
            subsId: id,
            encryptedBody,
          }).unwrap();
          if (response?.statusCode === 200) {
            setLoading(false);
            showToast(translate?.error?.planUpdate || "");
            navigate("/manage-subscription");
          } else {
            setLoading(false);
            showError(response?.message || "");
          }
        } catch (error: any) {
          setLoading(false);
          showError(error?.data?.message || "");
          console.log(error);
        }
      } else {
        try {
          setLoading(true);
          let encryptedBody = generateEncryptedKeyBody(data) as CommonBody;
          const response = await addSubscription(encryptedBody).unwrap();
          if (response?.statusCode === 200) {
            setLoading(false);
            showToast(translate?.error?.planAdd || "");
            formik.resetForm();
            navigate("/manage-subscription");
          } else {
            setLoading(false);
            showError(response?.message || "");
          }
        } catch (error: any) {
          setLoading(false);
          showError(error?.data?.message || "");
          console.log(error);
        }
      }
    },
  });

  const handleValidityUnitChange = (event: SelectChangeEvent) => {
    const unit = event.target.value as
      | "months"
      | "days"
      | "hours"
      | "minutes";
    setValidityUnit(unit);
    setReceiver("");
    setReceiverAr("");
    formik.setFieldValue("validity", "");
  };

  const handleReceiverChange = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    setReceiver(value);
    setReceiverAr(timeDurations[value] || "");
    formik.setFieldValue("validity", Number(value));
  };

  const handleReceiverChangeAr = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    setReceiverAr(value);
    const numericValue = timeDurations2[value];
    setReceiver(numericValue);
    formik.setFieldValue("validity", Number(numericValue));
  };

  const formatNumberWithCommas = (number: any) => {
    if (number === "" || number === null || number === undefined) {
      return "";
    }
    const numeric = Number(number);
    if (Number.isNaN(numeric) || numeric < 0) {
      return "";
    }
    // Allow Free plan price 0 (previously only showed values > 0)
    return numeric.toLocaleString("en-US");
  };

  const handlePriceChange = (event: any) => {
    const inputValue = event.target.value;
    // Remove non-numeric characters except decimal point
    const cleaned = String(inputValue).replace(/[^\d.]/g, "");
    if (cleaned === "" || cleaned === ".") {
      formik.setFieldValue("price", "");
      return;
    }
    const newValue = parseFloat(cleaned);
    if (!isNaN(newValue) && newValue >= 0) {
      formik.setFieldValue("price", newValue);
    }
  };

  const getCategoryDetail = async (id: string | undefined) => {
    try {
      const res :any= await subscriptionDetails({ subId: id }).unwrap();
      if (res?.statusCode === 200) {
        setSubsById(res?.data);
        const allowedUnits = ["months", "days", "hours", "minutes"] as const;
        const rawUnit = res?.data?.validityUnit as
          | (typeof allowedUnits)[number]
          | undefined;
        const unit = allowedUnits.includes(rawUnit as any)
          ? (rawUnit as (typeof allowedUnits)[number])
          : "months";
        setValidityUnit(unit);
        setReceiver(String(res?.data?.validity ?? ""));
        setReceiverAr(res?.data?.validity_ar ?? "");
        setFeatureEnabled(res?.data?.feature);
        formik.setFieldValue("validity", Number(res?.data?.validity));
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const label = { inputProps: { "aria-label": "Switch demo" } };
  const planFeatures = [
    {
      id: 1,
      name: translate?.main?.Showrequesttoagents || "",
      enable: true,
    },
  ];

  useEffect(() => {
    if (id && id !== "add") {
      getCategoryDetail(id);
    }
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_loyout">
        <div className="dashboard">
          {id && id !== "add" ? (
            <h1 className="mn_hdng">{translate?.main?.EditSubscriptionPlan}</h1>
          ) : (
            <h1 className="mn_hdng">{translate?.main?.AddSubscriptionPlan}</h1>
          )}
          <Button
            className="btn btn_primary"
            onClick={() => {
              navigate("/manage-subscription");
            }}
          >
            {translate?.main?.Back}
          </Button>
        </div>
        <Card className="cards">
          <form onSubmit={formik.handleSubmit}>
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">
                    {translate?.main?.PlanName} ({translate?.main?.English})
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    name="planName"
                    variant="outlined"
                    fullWidth
                    className="text_field"
                    placeholder={translate?.subscription?.planNameEn || ""}
                    onBlur={formik.handleBlur}
                    value={formik.values.planName}
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isString(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    helperText={
                      formik.touched.planName && formik.errors.planName
                    }
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">
                    {translate?.main?.PlanName} ({translate?.main?.Arabic})
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    name="planName_ar"
                    variant="outlined"
                    fullWidth
                    className={withArabicFieldClass("text_field")}
                    inputProps={arabicInputProps}
                    placeholder={translate?.subscription?.planNameAr || ""}
                    onBlur={formik.handleBlur}
                    value={formik.values.planName_ar}
                    onChange={(val) => {
                      const value = val.target.value;
                      // Prevent spaces-only input
                      if (value.trim().length === 0 && value.length > 0) {
                        return; // Don't update if it's only spaces
                      }
                      if (isString(value)) {
                        formik.handleChange(val);
                      }
                    }}
                    helperText={
                      formik.touched.planName_ar && formik.errors.planName_ar
                    }
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">
                    {translate?.main?.ValidityUnit || "Validity Unit"}
                  </Typography>
                  <FormControl sx={{ width: "100%" }}>
                    <Select
                      fullWidth
                      className="select_div"
                      value={validityUnit}
                      displayEmpty
                      onChange={handleValidityUnitChange}
                      inputProps={{
                        "aria-label": "Without label",
                      }}
                    >
                      <MenuItem value="months">
                        {translate?.main?.Months || "Months"}
                      </MenuItem>
                      <MenuItem value="days">
                        {translate?.main?.Days || "Days"}
                      </MenuItem>
                      <MenuItem value="hours">
                        {translate?.main?.Hours || "Hours"}
                      </MenuItem>
                      <MenuItem value="minutes">
                        {translate?.main?.Minutes || "Minutes"}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12} />
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">
                    {translate?.main?.PlanValidity} ({translate?.main?.English})
                  </Typography>
                  <FormControl sx={{ width: "100%" }}>
                    <Select
                      fullWidth
                      className="select_div"
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={receiver}
                      inputProps={{
                        "aria-label": "Without label",
                      }}
                      displayEmpty
                      onChange={handleReceiverChange}
                    >
                      <MenuItem value="" disabled>
                        {translate?.dashboard?.Select}
                      </MenuItem>
                      {activeDuration.values.map((value) => {
                        const singular =
                          validityUnit === "minutes"
                            ? translate?.main?.Minute || "Minute"
                            : validityUnit === "hours"
                              ? translate?.main?.Hour || "Hour"
                              : validityUnit === "days"
                                ? translate?.main?.Day || "Day"
                                : translate?.main?.Month;
                        const plural =
                          validityUnit === "minutes"
                            ? translate?.main?.Minutes || "Minutes"
                            : validityUnit === "hours"
                              ? translate?.main?.Hours || "Hours"
                              : validityUnit === "days"
                                ? translate?.main?.Days || "Days"
                                : translate?.main?.Months;
                        return (
                          <MenuItem key={value} value={String(value)}>
                            {value} {value === 1 ? singular : plural}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {error && !receiver ? (
                      <h6 className="err_msg">
                        {translate?.main?.PleaseselectValidity}
                      </h6>
                    ) : (
                      ""
                    )}
                  </FormControl>
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">
                    {translate?.main?.PlanValidity} ({translate?.main?.Arabic})
                  </Typography>
                  <FormControl sx={{ width: "100%" }} className={ARABIC_FIELD_CLASS}>
                    <Select
                      fullWidth
                      className="select_div"
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={receiverAr}
                      inputProps={{
                        "aria-label": "Without label",
                      }}
                      displayEmpty
                      onChange={handleReceiverChangeAr}
                    >
                      <MenuItem value="" disabled>
                        {translate?.dashboard?.Select}
                      </MenuItem>
                      {activeDuration.values.map((value) => (
                        <MenuItem key={value} value={activeDuration.ar[value]}>
                          {activeDuration.ar[value]}
                        </MenuItem>
                      ))}
                    </Select>
                    {error && !receiverAr ? (
                      <h6 className="err_msg">
                        {translate?.main?.PleaseselectValidity}
                      </h6>
                    ) : (
                      ""
                    )}
                  </FormControl>
                </Grid>
                

                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">
                    {translate?.main?.Description} {translate?.main?.English}
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    className="text_field"
                    name="description"
                    fullWidth
                    multiline
                    placeholder={translate?.subscription?.descEn || ""}
                    minRows={4}
                    maxRows={6}
                    onBlur={formik.handleBlur}
                    inputProps={
                      {
                        maxLength:450
                      }
                      }
                    value={formik.values.description}
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isString(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    helperText={
                      formik.touched.description && formik.errors.description
                    }
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">
                    {translate?.main?.Description} {translate?.main?.Arabic}
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    className={withArabicFieldClass("text_field")}
                    name="description_ar"
                    fullWidth
                    multiline
                    placeholder={translate?.subscription?.descAr || ""}
                    minRows={4}
                    maxRows={6}
                    inputProps={{
                      maxLength: 450,
                      ...arabicInputProps,
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.description_ar}
                    onChange={(val) => {
                      const value = val.target.value;
                      // Prevent spaces-only input
                      if (value.trim().length === 0 && value.length > 0) {
                        return; // Don't update if it's only spaces
                      }
                      if (isString(value)) {
                        formik.handleChange(val);
                      }
                    }}
                    helperText={
                      formik.touched.description_ar &&
                      formik.errors.description_ar
                    }
                  />
                </Grid>

                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">
                    {" "}
                    {translate?.main?.PlanPrice}{" "}
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    name="price"
                    variant="outlined"
                    fullWidth
                    className="text_field"
                    placeholder={translate?.subscription?.planPrice || ""}
                    onBlur={formik.handleBlur}
                    value={formatNumberWithCommas(formik.values.price)}
                    // onChange={(val) => {
                    // if (
                    //   val.target.value === " " ||
                    //   val.target.value === "."
                    // ) {
                    // } else{
                    // }
                    // formik.handleChange(val);
                    // }}
                    onChange={handlePriceChange}
                    helperText={formik.touched.price && formik.errors.price}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    component="h2"
                    sx={{
                      fontSize: "20px",

                      fontWeight: 700,
                    }}
                    // className="custom_label"
                  >
                    {translate?.main?.Feature}
                  </Typography>
                </Grid>

                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">
                    {translate?.main?.NormalCredits}
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    name="normalListing"
                    variant="outlined"
                    fullWidth
                    className="text_field"
                    placeholder={translate?.main?.NormalCredits}
                    onBlur={formik.handleBlur}
                    value={formik.values.normalListing}
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isNumber(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    helperText={
                      formik.touched.normalListing &&
                      formik.errors.normalListing
                    }
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">
                    {translate?.main?.HighlightedCredits}
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    name="highlightedListing"
                    variant="outlined"
                    fullWidth
                    className="text_field"
                    placeholder={translate?.subscription?.highlightedCredits || ""}
                    onBlur={formik.handleBlur}
                    value={formik.values.highlightedListing}
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isNumber(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    helperText={
                      formik.touched.highlightedListing &&
                      formik.errors.highlightedListing
                    }
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Typography className="custom_label">
                    {translate?.main?.ManageRequest}
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    name="acceptRequest"
                    variant="outlined"
                    fullWidth
                    className="text_field"
                    placeholder={translate?.subscription?.acceptRequest || ""}
                    onBlur={formik.handleBlur}
                    value={formik.values.acceptRequest}
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isNumber(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    helperText={
                      formik.touched.acceptRequest &&
                      formik.errors.acceptRequest
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", gap: "10px", alignItems: "center" }}
                  >
                    <Typography
                      component="h2"
                      sx={{
                        fontSize: "20px",

                        fontWeight: 700,
                      }}
                      // className="custom_label"
                    >
                      {translate?.main?.Showrequesttoagents}
                    </Typography>

                    <Switch
                      {...label}
                      size="small"
                      checked={featureEnabled}
                      onChange={handleSwitchChange}
                    />
                  </Box>
                </Grid>
              </Grid>
              <div className="form_btn">
                <Button
                  size="large"
                  type="submit"
                  className="btn btn_primary"
                  onClick={() => setError(true)}
                >
                  {translate?.main?.Save}
                </Button>
              </div>
            </CardContent>
          </form>
        </Card>
      </div>
    </MainContainer>
  );
};

export default AddSubscriptionPlan;